import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { formatDisplayHoursToMinutes, formatHours } from '@app/shared/utils/utils'

import { Role } from '@app/features/scoping/models/role.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { UpdateRolePayload, ScopeElementsConfig } from '../models'
import { plainToInstance } from 'class-transformer'
import { TableColumnKey } from '@app/shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { getCrFieldToDisplay } from '../utils/change-request/get-cr-field-to-display.util'
import { RoleWithChanges } from '../models/entities-with-changes/role-with-changes.interface'
import { getCrNetChanges, showCrDifferences, showCrNetChanges } from '../utils/change-request'
import { FormatHoursPipe, formatHoursPipeFn } from '@app/shared/pipe/formatHours.pipe'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { Money } from '@app/features/scope-overview/model/money.model'
import { formatCrPrice } from '../utils/format-value.utils'

@Component({
  selector: 'scope-roles-row',
  templateUrl: './roles-row.component.html',
  styleUrls: ['./roles-row.component.scss'],
  providers: [FormatHoursPipe],
})
export class RolesRowComponent {
  @Input() level: number
  @Input() role: RoleWithChanges
  @Input() config: ScopeElementsConfig
  @Input() component: ScopeComponent

  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  constructor(
    protected scopeElementsService: ScopeElementsService,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  overrideRateCardRoleHours() {
    return () => this.onOverrideRateCardRoleHours.emit({ component: this.component, role: this.role })
  }

  updateRoleHours(rateCardMinutes: number) {
    const role = plainToInstance(Role, { ...this.role, rateCardMinutes })
    this.onUpdateRole.emit({ component: this.component, role })
  }

  formatDisplayValue = (val) => formatHours(val / 60)
  parseDisplayValue = (val) => formatDisplayHoursToMinutes(val)

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<RoleWithChanges>(fieldName, this.role)
  showDiff = (fieldName: string): boolean => showCrDifferences<RoleWithChanges>(this.config, this.role, fieldName)

  formatPrice = (value: number | Money): string => formatCrPrice(value, this.config?.currency, this.convertCurrencyPipe)
  protected readonly formatHours = formatHoursPipeFn

  showNetChanges = (fieldName: string) =>
    showCrNetChanges(this.config, this.isElementAdded || this.isElementRemoved, this.role.changes?.[fieldName])
  getNetChanges = (fieldName: string, defaultValue: number) =>
    getCrNetChanges(this.role.changes, fieldName, defaultValue, this.isElementAdded, this.isElementRemoved)

  @HostBinding('class.change-request-removed') get isElementRemoved() {
    return this.config?.isChangeRequest && (this.config?.showDiff || this.config?.showNetDelta) && this.role.isRemoved
  }
  @HostBinding('class.change-request-added') get isElementAdded() {
    return (
      this.config?.isChangeRequest &&
      (this.config?.showDiff || this.config?.showNetDelta) &&
      (this.role.isAdded || !this.role.id)
    )
  }

  protected readonly TableColumnKey = TableColumnKey
  protected readonly Privilege = Privilege
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
